import { Clock, Send } from 'lucide-react'
import { useState } from 'react'
import { ReportRecipientsSection } from '@/components/reports/ReportRecipientsSection'
import { EmptyState } from '@/components/ui/EmptyState'
import { Modal } from '@/components/ui/Modal'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { Skeleton } from '@/components/ui/Skeleton'
import { Toggle } from '@/components/ui/Toggle'
import { useCanReadFinance, useIsAdmin } from '@/lib/auth'
import { useHoursReport, useReportHistory, useSendHoursReport, useUpdateReportSettings } from '@/lib/report-api'
import type { HoursReportClient, HoursReportProject } from '@/lib/report-types'

function formatDate(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function ReportClientPanel({
  client,
  project,
  onSelectProject,
  onClose,
}: {
  client: HoursReportClient
  project: HoursReportProject
  onSelectProject: (projectId: string) => void
  onClose: () => void
}) {
  const canSend = useIsAdmin()
  const canEditSettings = useCanReadFinance()
  const [fromDate, setFromDate] = useState<string | null>(null)
  const reportQuery = useHoursReport(client.clientId, project.projectId, fromDate)
  const historyQuery = useReportHistory(client.clientId, project.projectId)
  const sendReport = useSendHoursReport()
  const updateSettings = useUpdateReportSettings()

  const multiProject = client.projects.length > 1
  const hasRecipients = project.recipients.length > 0
  const anyProjectHasRecipients = client.projects.some((p) => p.reportEnabled && p.recipients.length > 0)

  const detail = reportQuery.data
  const nothingToSend = !!detail && detail.newLinesSince === 0 && detail.travelCount === 0

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title={multiProject ? `Urenrapport ${client.clientName} - ${project.projectName}` : `Urenrapport ${client.clientName}`}
      description={canEditSettings ? 'Ontvangers en wekelijkse verzending beheer je hier.' : 'Ontvangers, wekelijkse verzending en verzendhistorie.'}
      className="w-[min(94vw,760px)]"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Sluiten</button>
          {canSend && multiProject ? (
            <button
              type="button"
              className="btn-secondary"
              disabled={!anyProjectHasRecipients || sendReport.isPending}
              title={anyProjectHasRecipients ? 'Verstuur de rapportmail van elk project met ontvangers en een aanstaand rapport' : 'Geen project met ontvangers en een aanstaand rapport'}
              onClick={() => sendReport.mutate({ clientId: client.clientId }, { onSuccess: onClose })}
            >
              <Send className="h-4 w-4" />
              Alle projecten versturen
            </button>
          ) : null}
          {canSend ? (
            <button
              type="button"
              className="btn-primary"
              disabled={!project.reportEnabled || !hasRecipients || nothingToSend || sendReport.isPending}
              title={
                !project.reportEnabled
                  ? 'Het urenrapport van dit project staat uit'
                  : !hasRecipients
                    ? 'Stel eerst ontvangers in'
                    : nothingToSend
                      ? 'Geen nieuwe uren om te rapporteren'
                      : undefined
              }
              onClick={() => sendReport.mutate({ clientId: client.clientId, projectId: project.projectId, fromDate }, { onSuccess: onClose })}
            >
              <Send className="h-4 w-4" />
              {multiProject ? 'Dit project versturen' : 'Nu versturen'}
            </button>
          ) : null}
        </>
      }
    >
      <div className="space-y-5">
        {multiProject ? (
          <SegmentedControl
            segments={client.projects.map((p) => ({ value: p.projectId, label: p.projectName }))}
            value={project.projectId}
            onChange={onSelectProject}
          />
        ) : null}
        <section className="flex items-center justify-between gap-4 rounded-lg border border-hairline p-3">
          <div>
            <p className="text-sm font-medium text-ink">{multiProject ? `Urenrapport voor ${project.projectName}` : 'Urenrapport voor dit project'}</p>
            <p className="text-xs text-ink-faint">
              {project.reportEnabled
                ? 'Dit project krijgt een rapportmail.'
                : 'Uit: dit project krijgt geen rapportmail, niet wekelijks en niet handmatig.'}
            </p>
          </div>
          <Toggle
            checked={project.reportEnabled}
            onChange={(next) =>
              updateSettings.mutate({ clientId: client.clientId, input: { projectEnabled: { [project.projectId]: next } } })
            }
            label={`Urenrapport voor ${project.projectName}`}
            disabled={!canEditSettings || updateSettings.isPending}
          />
        </section>
        <ReportRecipientsSection client={client} canEdit={canEditSettings} />

        <section>
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="label-caps text-ink-faint">Voorbeeld</p>
            <label className="flex items-center gap-2 text-xs text-ink-faint">
              Uren vanaf
              <input
                type="date"
                value={fromDate ?? detail?.fromDate ?? ''}
                onChange={(e) => setFromDate(e.target.value || null)}
                className="h-8 rounded-lg border border-hairline bg-surface px-2 text-sm text-ink outline-hidden"
              />
              {detail ? <span>t/m {formatDate(detail.untilDate)}</span> : null}
              {fromDate && detail && fromDate !== detail.defaultFromDate ? (
                <button type="button" className="text-accent-700 hover:underline" onClick={() => setFromDate(null)}>
                  standaard
                </button>
              ) : null}
            </label>
          </div>
          <div className="overflow-hidden rounded-lg border border-hairline">
            {detail ? (
              <iframe title="Rapport" srcDoc={detail.html} className="h-80 w-full border-0 bg-white" sandbox="" />
            ) : (
              <Skeleton className="h-80 w-full" />
            )}
          </div>
          {detail ? (
            <p className="mt-1.5 text-xs text-ink-faint" data-testid="report-sendable-summary">
              {detail.newLinesSince} verzendbare regel{detail.newLinesSince === 1 ? '' : 's'}
              {detail.travelCount > 0 ? ` · ${detail.travelCount} reisregel${detail.travelCount === 1 ? '' : 's'}` : ''}
              {detail.provisionalCount > 0 ? ` · ${detail.provisionalCount} als concept (nog niet geaccordeerd)` : ''}
            </p>
          ) : null}
        </section>

        <section>
          <p className="label-caps mb-2 text-ink-faint">Verzendhistorie</p>
          {historyQuery.isLoading ? (
            <Skeleton className="h-20 w-full" />
          ) : !historyQuery.data || historyQuery.data.length === 0 ? (
            <EmptyState icon={Clock} title="Nog niets verzonden" description="Zodra je een rapport verstuurt verschijnt het hier." />
          ) : (
            <div className="divide-y divide-hairline">
              {historyQuery.data.map((row) => (
                <div key={row.id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <span className="font-medium text-ink">{formatDate(row.periodStart)} t/m {formatDate(row.periodEnd)}</span>
                    <span className="ml-2 text-xs text-ink-faint">{row.sentTo.join(', ') || 'geen ontvangers'}</span>
                  </div>
                  <span className="text-xs text-ink-faint">{row.createdByName ?? 'Automatisch'}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
  )
}
